import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Search, X, type LucideIcon } from 'lucide-react'
import { BottomSheet, useBottomSheet } from '@/components/ui/bottom-sheet'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface SectionNavItem<V extends string = string> {
  value: V
  label: string
  icon?: LucideIcon
  /** The key of a group in `groups`. */
  group?: string
  /** A small mark after the label (a count, a review state). */
  badge?: React.ReactNode
  /** Shown under the label while searching, and searched. */
  description?: string
  /** Extra words the search finds the item by (never shown). */
  keywords?: string
}

export interface SectionNavSearch {
  placeholder: string
  /** The text when nothing matches the query. */
  empty: (query: string) => string
}

export interface SectionNavProps<V extends string = string> {
  items: SectionNavItem<V>[]
  value: V
  onChange: (value: V) => void
  'aria-label': string
  /** Headings over the items, in this order; items without a group come first. */
  groups?: Array<{ key: string; label: string }>
  /** A search box over the sections (label, description, keywords, group): the Settings menu. */
  search?: SectionNavSearch
  className?: string
  'data-testid'?: string
}

/**
 * Default for `SectionLayout`'s `sideMinWidth`: the content area width from
 * which the list goes on the side (200 px column + 24 px gap, leaving the
 * content at least ~496 px). APP-171 measured the container; 1100 px sent a
 * desktop window with the app menu open to the phone sheet, so it came down.
 */
export const SECTION_NAV_SIDE_MIN_WIDTH = 720

/** Below this viewport width the compact list opens as a bottom sheet (phones); above, as a popover. */
const PHONE_MAX_WIDTH = 640
const PHONE_QUERY = `(max-width: ${PHONE_MAX_WIDTH - 0.02}px)`

const usePhone = () => {
  const [phone, setPhone] = React.useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(PHONE_QUERY).matches)
  React.useEffect(() => {
    const mq = window.matchMedia?.(PHONE_QUERY)
    if (!mq) return
    const update = () => setPhone(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  return phone
}

/**
 * Whether the enclosing `SectionLayout` has room for the list on the side.
 * `null` outside a layout: the nav then falls back to the 1024 px viewport rule.
 */
const SectionSideContext = React.createContext<boolean | null>(null)

/**
 * The sections of a page with 5 or more areas (F-182, layer 1), born from the
 * Settings menu. Three forms:
 * - a list on the side when the content area of its `SectionLayout` is at
 *   least `sideMinWidth` wide (measured, not the viewport, so the app's own
 *   side menu counts);
 * - below that, a button with the current section on top of the content,
 *   which opens the same groups in a popover anchored to it (tablet, narrow
 *   desktop);
 * - on phones (viewport under 640 px), the same button opening a bottom sheet.
 */
export function SectionNav<V extends string = string>({ items, value, onChange, groups, search, className, ...rest }: SectionNavProps<V>) {
  const { t } = useTranslation('filters')
  const sheet = useBottomSheet()
  const phone = usePhone()
  const [popoverOpen, setPopoverOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const current = items.find((i) => i.value === value) ?? items[0]
  const CurrentIcon = current?.icon
  const testId = rest['data-testid'] ?? 'section-nav'
  const side = React.useContext(SectionSideContext)
  // Classes for the compact (button) and side (list) forms: measured inside a
  // layout, by viewport otherwise.
  const compactOnly = side === null ? 'lg:hidden' : side ? 'hidden' : ''
  const sideOnly = side === null ? 'hidden lg:block' : side ? '' : 'hidden'

  const q = search ? normalize(query) : ''
  const matches = React.useMemo(() => {
    if (!q) return items
    const words = q.split(/\s+/)
    return items.filter((item) => {
      const groupLabel = groups?.find((g) => g.key === item.group)?.label ?? ''
      const haystack = normalize([item.label, item.description, item.keywords, groupLabel].filter(Boolean).join(' '))
      return words.every((word) => haystack.includes(word))
    })
  }, [q, items, groups])

  const blocks: Array<{ key: string; label?: string; items: SectionNavItem<V>[] }> = [
    { key: '__none', items: matches.filter((i) => !i.group || !groups?.some((g) => g.key === i.group)) },
    ...(groups ?? []).map((g) => ({ key: g.key, label: g.label, items: matches.filter((i) => i.group === g.key) })),
  ].filter((b) => b.items.length > 0)

  const choose = (v: V) => { setQuery(''); onChange(v) }

  const list = (pick: (v: V) => void, inSheet: boolean) => matches.length === 0 ? (
    <p className="px-3 py-2 text-sm text-gray-500" data-testid={`${testId}-search-empty`}>{search?.empty(query)}</p>
  ) : (
    <div className="space-y-5">
      {blocks.map((block) => (
        <div key={block.key}>
          {block.label && <p className="mb-1 px-3 text-xs font-medium uppercase tracking-wide text-gray-400">{block.label}</p>}
          <ul className="space-y-0.5">
            {block.items.map(({ value: itemValue, label, icon: Icon, badge, description }) => {
              const active = itemValue === value
              return (
                <li key={itemValue}>
                  <button
                    type="button"
                    onClick={() => pick(itemValue)}
                    aria-current={active ? 'page' : undefined}
                    data-testid={`${testId}-${itemValue}`}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-lg px-3 text-left text-sm transition-colors',
                      inSheet ? 'min-h-11 py-2' : 'py-2',
                      active ? 'bg-primary-50 font-medium text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                    )}
                  >
                    {Icon && <Icon className={cn('h-4 w-4 flex-shrink-0', active ? 'text-primary-600' : 'text-gray-400')} aria-hidden="true" />}
                    {/* Up to two lines (the side menu is 200 px wide): a long label wraps instead of being cut. */}
                    <span className="min-w-0 flex-1 line-clamp-2 break-words leading-snug">{label}</span>
                    {badge}
                  </button>
                  {q && description && <p className="truncate px-3 pb-1 pl-9 text-xs text-gray-500">{description}</p>}
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </div>
  )

  const searchBox = search && (
    <div className="relative mb-4">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
      <input
        type="text"
        role="searchbox"
        enterKeyHint="search"
        /* Typing a search is not an unsaved change. */
        data-guard-ignore
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && matches[0]) choose(matches[0].value); if (e.key === 'Escape') setQuery('') }}
        placeholder={search.placeholder}
        aria-label={search.placeholder}
        className="h-10 w-full rounded-md border border-gray-300 bg-white pl-9 pr-9 text-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-200"
        data-testid={`${testId}-search`}
      />
      {query && (
        <button
          type="button"
          onClick={() => setQuery('')}
          aria-label={t('clearSearch')}
          className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  )

  const triggerClass = 'flex h-11 items-center gap-2.5 rounded-lg border border-gray-300 bg-white px-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
  const triggerContent = (
    <>
      {CurrentIcon && <CurrentIcon className="h-4 w-4 flex-shrink-0 text-primary-600" aria-hidden="true" />}
      <span className="min-w-0 flex-1 truncate">{current?.label}</span>
      <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
    </>
  )

  return (
    <div className={className}>
      {searchBox}
      {/* Phone and tablet, while searching: the matches in place of the button. */}
      {q && (
        <nav aria-label={rest['aria-label']} className={cn('rounded-xl bg-white p-2 shadow-sm', compactOnly)} data-testid={`${testId}-results`}>
          {list(choose, true)}
        </nav>
      )}
      {/* Phone: the current section, full width; the list opens from the bottom. */}
      {phone ? (
        <>
          <button
            type="button"
            onClick={sheet.show}
            aria-haspopup="dialog"
            aria-label={`${rest['aria-label']}: ${current?.label ?? ''}`}
            className={cn(triggerClass, 'w-full', compactOnly, q && 'hidden')}
            data-testid={`${testId}-trigger`}
          >
            {triggerContent}
          </button>
          <BottomSheet sheet={sheet} title={rest['aria-label']} data-testid={`${testId}-sheet`}>
            <nav aria-label={rest['aria-label']} className="px-3 pb-2">
              {/* Changed once the sheet's history entry is gone, so the address keeps the section. */}
              {list((v) => sheet.hide(() => onChange(v)), true)}
            </nav>
          </BottomSheet>
        </>
      ) : (
        /* Tablet and narrow desktop: a select-like button opening the list right under it. */
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`${rest['aria-label']}: ${current?.label ?? ''}`}
              className={cn(triggerClass, 'w-auto min-w-[16rem] max-w-full', compactOnly, q && 'hidden')}
              data-testid={`${testId}-trigger`}
            >
              {triggerContent}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="max-h-[min(32rem,var(--radix-popover-content-available-height))] w-72 overflow-y-auto p-2"
            data-testid={`${testId}-popover`}
          >
            <nav aria-label={rest['aria-label']}>
              {list((v) => { setPopoverOpen(false); onChange(v) }, false)}
            </nav>
          </PopoverContent>
        </Popover>
      )}

      <nav aria-label={rest['aria-label']} className={cn(sideOnly)} data-testid={testId}>
        {list(choose, false)}
      </nav>
    </div>
  )
}

const normalize = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()

/**
 * A page with a `SectionNav`: the list on the side (200 px, stuck to the top)
 * when this container is at least `sideMinWidth` wide, a compact control above
 * the content below that. Measured with a `ResizeObserver`, like the Feed's two
 * columns, so the app menu (open or closed) counts.
 */
export function SectionLayout({ nav, children, className, sideMinWidth = SECTION_NAV_SIDE_MIN_WIDTH }: {
  nav: React.ReactNode
  children: React.ReactNode
  className?: string
  /** Minimum width of the content area for the list on the side, in px. */
  sideMinWidth?: number
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const [side, setSide] = React.useState(false)

  React.useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const update = () => setSide(node.getBoundingClientRect().width >= sideMinWidth)
    update()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => observer.disconnect()
  }, [sideMinWidth])

  return (
    <SectionSideContext.Provider value={side}>
      <div ref={ref} className={cn(side && 'grid grid-cols-[200px,minmax(0,1fr)] gap-6', className)} data-section-side={side ? 'true' : 'false'}>
        <aside className={side ? 'sticky top-6 self-start' : 'mb-6'}>{nav}</aside>
        <div className="min-w-0">{children}</div>
      </div>
    </SectionSideContext.Provider>
  )
}

export interface SectionPill {
  key: string
  label: string
  icon?: React.ReactNode
  /** A count or a dot, placed on the pill's corner. */
  badge?: React.ReactNode
  active?: boolean
  /** The pill opens a sheet (its spaces) instead of going straight to a page. */
  opensSheet?: boolean
  testId?: string
}

/**
 * F-191 phase 5 (§4.5.2): a horizontal strip of section pills (icon and a
 * short label), the phone form of the bar on top. The strip scrolls sideways
 * and the page does not; a fade on the right edge says more pills follow, and
 * the current one is brought into view. Each pill is a 44 px touch target.
 */
export function SectionPills({ items, onSelect, className, ...rest }: {
  items: SectionPill[]
  onSelect: (key: string) => void
  'aria-label': string
  className?: string
  'data-testid'?: string
}) {
  const scroller = React.useRef<HTMLUListElement>(null)
  const [fade, setFade] = React.useState({ start: false, end: false })
  const activeKey = items.find((item) => item.active)?.key

  const measure = React.useCallback(() => {
    const node = scroller.current
    if (!node) return
    setFade({ start: node.scrollLeft > 1, end: node.scrollLeft + node.clientWidth < node.scrollWidth - 1 })
  }, [])

  React.useLayoutEffect(() => {
    const node = scroller.current
    if (!node) return
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [measure, items.length])

  /* The current section in view (centered when it can be), without moving the page. */
  React.useEffect(() => {
    const node = scroller.current
    const pill = activeKey ? node?.querySelector<HTMLElement>(`[data-pill="${CSS.escape(activeKey)}"]`) : null
    if (!node || !pill) return
    const left = pill.offsetLeft - (node.clientWidth - pill.offsetWidth) / 2
    node.scrollLeft = Math.max(0, left)
    measure()
  }, [activeKey, measure])

  return (
    <nav aria-label={rest['aria-label']} className={cn('relative', className)} data-testid={rest['data-testid']}>
      <ul ref={scroller} onScroll={measure} className="flex h-full items-center gap-1 overflow-x-auto px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item) => (
          <li key={item.key} className="flex-shrink-0" data-pill={item.key}>
            <button
              type="button"
              onClick={() => onSelect(item.key)}
              aria-current={item.active ? 'page' : undefined}
              aria-haspopup={item.opensSheet ? 'dialog' : undefined}
              title={item.label}
              className="group flex min-h-11 items-center rounded-full focus-visible:outline-none"
              data-testid={item.testId}
            >
              <span className={cn(
                'relative flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium transition-colors group-focus-visible:ring-2 group-focus-visible:ring-primary',
                item.active ? 'bg-primary-600 text-primary-foreground' : 'bg-muted text-foreground-secondary group-hover:text-foreground'
              )}>
                {item.icon}
                <span className="max-w-[14ch] truncate">{item.label}</span>
                {item.badge}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {fade.start && <span aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-[rgb(var(--rgb-surface))] to-transparent" />}
      {fade.end && <span aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[rgb(var(--rgb-surface))] to-transparent" data-pill-fade />}
    </nav>
  )
}
